/**
 * Saunter Haus design source of truth. Read before changing the public website.
 * This component supplies CSS tokens only; it is not a visible style-guide page.
 * Record user-approved design changes here alongside the consuming styles.
 */
export const styleTokens = Object.freeze({
  sage: "#b0b6a5",
  "sage-dark": "#4b5847",
  ivory: "#dad5c7",
  ink: "#1c150c",
  stone: "#aaa9a6",
  oak: "#70624d",
  paper: "#f7f7f3",
  mist: "#e5e6e2",
  forest: "#253023",
  "text-muted": "#555951",
  "font-display": "Georgia, 'Times New Roman', serif",
  "font-body": 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  "type-hero": "62px",
  "type-hero-mobile": "42px",
  "type-heading": "44px",
  "type-heading-mobile": "34px",
  "type-subheading": "27px",
  "shell-width": "1328px",
  "gutter-desktop": "48px",
  "gutter-mobile": "24px",
  "section-mobile": "60px",
  "section-generous": "100px",
  "motion-control": "600ms",
  "motion-image": "1400ms",
  "motion-enter": "2s",
  "editorial-image-opacity": "0.20",
});

export const styleRules = Object.freeze({
  identity: [
    "Interior design, home staging, furniture curation, and styling studio.",
    "Collected, tactile, personal, and quietly luxurious. Saunter means to move slowly; communicate that through the experience.",
    "Keep the richness and useful sections. Do not reduce the site to an empty minimal template.",
    "Use the supplied dark-green sh monogram at public/favicon.png for the browser favicon and Apple touch icon. Preserve the original brand artwork.",
  ],
  palette: [
    "Sage is a visible brand color: announcement strip, service bands, and accents.",
    "Pair sage with paper, mist, ivory, ink, and dark forest for balanced contrast.",
    "The three-column styling band uses dark sage with paper-colored headings, body text, and icons, plus sage dividers. Preserve this darker pause between the faded process and inquiry sections.",
    "Oak belongs primarily in materials and photography. Avoid an overwhelmingly beige or brown interface.",
    "Use the exported CSS tokens for new shared styles; do not invent a separate palette per page.",
  ],
  typography: [
    "Georgia serif for editorial headings; system sans-serif for body text and controls.",
    "Use fixed desktop/mobile type sizes, zero letter spacing, and comfortable line heights.",
    "Reserve hero-scale type for the hero. Keep labels and supporting text quiet but readable.",
  ],
  layout: [
    "Center the header logo against the full viewport width, regardless of side content widths.",
    "Reuse GlobalHeader, GlobalFooter, StudioHero, studio-shell, eyebrow, and text-link.",
    "Use full-width section bands and generous breathing room. Align repeated image grids on the same top edge, with equal image proportions and aligned captions; do not stagger them.",
    "Avoid nested cards, decorative floating panels, and rounded pill controls.",
    "Keep a hint of the next section visible below the hero on desktop and mobile.",
  ],
  imagery: [
    "Natural daylight, sage plaster, aged oak, antique mirrors, linen, stone, ceramics, and patina.",
    "Show inspectable rooms and material texture. Avoid generic glass-house interiors, heavy dark overlays, blur, or decorative gradients.",
    "The current generated concept hero is /images/saunter-editorial-hero.png. Never present it or stock imagery as completed client work.",
    "Keep real project photographs and inspirational imagery clearly distinguished when adding the portfolio.",
    "The Inspiration grid uses original generated living, bedroom, and dining concepts: contemporary editorial rooms with mixed wood finishes, sage accents, layered textiles, and believable asymmetry. Avoid matching furniture sets, dated generic stock rooms, and exterior architecture in this interior-focused collection.",
    "Use supporting photography in services and notebook sections so the experience stays visually rich beyond the hero.",
    "Services photography sits above an opaque sage text band. Never blend a busy photograph behind small service text. Editorial detail images remain clear and unfiltered.",
    "Process and inquiry use decorative full-width photographs at 20% opacity over paper, with dark #263222 supporting text and ink headings. Reuse photo-band and photo-backdrop. Do not apply opacity to the content. This combination guarantees at least 7.82:1 text contrast even over black image pixels. Keep the inquiry form and fields opaque with persistent labels.",
  ],
  motion: [
    "Use gentle image settling, small hover shifts, and unhurried transitions; no scroll-jacking or forced delays.",
    "Respect prefers-reduced-motion. Motion must never block content or navigation.",
  ],
  copy: [
    "The header logo carries the brand name; do not repeat Saunter Haus as the hero headline. Let the photograph lead, with one short editorial heading, a quiet service descriptor, and one purposeful link.",
    "Current home hero: 'A home, considered.' Studio hero: 'Spaces with character.' Avoid stacked slogans, oversized duplicate branding, and ornamental photo captions.",
    "Warm, concise, assured, and specific to homes, materials, and the way people live.",
    "Let luxury be felt. Do not label the brand 'slow luxury' or explain the design choices in public copy.",
    "No shopping-cart language, internal implementation notes, migration explanations, or draft promises on public pages.",
  ],
  boundaries: [
    "Keep primary navigation to Services, Inspiration, and Inquire, using siteNavigation.js in both header and footer. The centered logo returns home.",
    "All Services entry points open /studio at the top, including homepage service cards and studio introduction links. Never use section hashes for Services entry points. Inspiration intentionally opens the homepage visual notebook at /#portfolio; inquiry links may still target /studio#contact. Repeated Services clicks on the current studio page must return to the top too.",
    "Group staging, curation, styling, and process within the services destination. Keep the notebook as homepage content, not a separate navigation item.",
    "Add a primary destination only when it has distinct, complete content. Call the current visual collection Inspiration until genuine client work supports Portfolio.",
    "Retain commerce modules as reference only; do not edit or expose them without a specific user request.",
    "Do not display these rules or add a public style-guide route.",
    "Do not introduce pretend interactions. New forms and buttons need working behavior or an honest visible state.",
  ],
  verification: [
    "Target WCAG 2.2 AA: text contrast at least 4.5:1 (3:1 for large text); interactive boundaries and focus indicators at least 3:1. Photo overlays require worst-case pixel contrast verification, not visual guessing or an automated pass alone.",
    "Provide a skip link, visible persistent form labels, native validation, visible focus, 44px primary control targets, and keyboard-operable modal navigation with contained focus and focus restoration.",
    "Check desktop/mobile framing, centered logo, text fit, image loading, and horizontal overflow.",
    "Check navigation, visible focus states, readable contrast, and reduced motion after relevant changes.",
    "Run the production build after shared changes and keep the local preview available.",
  ],
});

const cssVariables = Object.entries(styleTokens)
  .map(([name, value]) => `--${name}: ${value};`)
  .join("\n");

export default function StyleRules() {
  return <style data-saunter-style-rules>{`:root {\n${cssVariables}\n}`}</style>;
}
